import * as z from "zod";
import Workspace, { WorkspaceFolder } from "@/models/workspace/Workspace";

/** Same limit as folder creation, see CreateFolderDialog. */
export const MAX_FOLDER_NAME_LENGTH = 50;

/** Shown on the name field when a sibling folder already carries the name. */
export const FOLDER_NAME_TAKEN_MESSAGE =
  "Der findes allerede en mappe med dette navn";

/**
 * Names of the folders that sit next to `folderId` — i.e. the other children of
 * its parent, or the other root folders when it has no parent. The folder itself
 * is excluded, so renaming it to its current name is not reported as a clash.
 *
 * The API rejects a rename that collides with a sibling name, so we mirror that
 * rule client-side to fail in the form instead of on the request.
 */
export const getSiblingFolderNames = (
  workspace: Workspace | null,
  folderId: string | undefined
): string[] => {
  if (!workspace || !folderId) return [];

  const findSiblings = (
    folders: WorkspaceFolder[]
  ): WorkspaceFolder[] | null => {
    if (folders.some((f) => f.id === folderId)) return folders;
    for (const folder of folders) {
      const nested = findSiblings(folder.folders);
      if (nested) return nested;
    }
    return null;
  };

  const siblings = findSiblings(workspace.folders);
  if (!siblings) return [];

  return siblings.filter((f) => f.id !== folderId).map((f) => f.name);
};

/**
 * Whether `name` is already taken by one of `siblingNames`, ignoring case and
 * surrounding whitespace — the API applies the same folding, so the form and the
 * request agree on what counts as a clash.
 */
export const hasSiblingNameClash = (
  siblingNames: string[],
  name: string
): boolean => {
  const candidate = name.trim().toLowerCase();
  return siblingNames.some(
    (sibling) => sibling.trim().toLowerCase() === candidate
  );
};

/**
 * The files URL addresses folders by name, and `currentPath` is rebuilt from
 * those names on every workspace change. Renaming a folder that sits on the
 * current path therefore leaves a segment in the URL that no longer resolves,
 * which collapses the path back to the workspace root.
 *
 * Returns the encoded folder path the URL should move to after the rename, or
 * `null` when the renamed folder is not on the path and the URL still holds.
 */
export const buildRenamedFolderPath = (
  currentPath: { id: string; name: string }[],
  folderId: string,
  newName: string
): string | null => {
  const renamedIndex = currentPath.findIndex(
    (segment) => segment.id === folderId
  );
  if (renamedIndex === -1) return null;

  return currentPath
    .map((segment, index) =>
      encodeURIComponent(index === renamedIndex ? newName : segment.name)
    )
    .join("/");
};

/**
 * Schema for the rename form. `siblingNames` must exclude the folder being
 * renamed (see {@link getSiblingFolderNames}). Surrounding whitespace is ignored
 * when validating; the caller trims before submitting.
 */
export const buildRenameFolderSchema = (siblingNames: string[]) => {
  return z.object({
    name: z
      .string()
      .refine((value) => value.trim().length > 0, { message: "Udfyld navn" })
      .refine((value) => value.trim().length <= MAX_FOLDER_NAME_LENGTH, {
        message: `Navn må højst være ${MAX_FOLDER_NAME_LENGTH} tegn`,
      })
      .refine((value) => !hasSiblingNameClash(siblingNames, value), {
        message: FOLDER_NAME_TAKEN_MESSAGE,
      }),
  });
};

export type RenameFolderFormValues = z.infer<
  ReturnType<typeof buildRenameFolderSchema>
>;
